class PromoScrollStrip extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.promo-scroll__track');
    this.segments = Array.from(this.querySelectorAll('.promo-scroll__progress-segment'));
    this.prevButton = this.querySelector('.promo-scroll__arrow--prev');
    this.nextButton = this.querySelector('.promo-scroll__arrow--next');
    if (!this.track) return;

    this.slides = Array.from(this.track.children);
    this.realCount = Number(this.track.dataset.realCount || this.slides.length);
    // Triple-copy belt when looping: [copy, real copy, copy] => 3 * realCount slides.
    this.looping = this.track.dataset.looping === 'true' && this.slides.length === this.realCount * 3;
    this.autoplayDelay = 5000;

    this.currentDom = this.domForReal(0);
    this.activeReal = 0;
    this.autoplayTimer = null;
    this.autoplayActive = false;
    this.isDragging = false;
    this.isProgrammatic = false;
    this.settleTimer = null;

    this.setupArrows();
    this.setupDrag();
    this.setupProgressClicks();
    this.setupWheel();
    this.setupScrollTracking();

    this.jumpToDom(this.currentDom);
    this.track.classList.add('is-ready');
    this.startAutoplay();

    // Hidden tabs freeze rAF but keep firing setTimeout, so autoplay would
    // keep advancing currentDom without any actual scrolling and walk the
    // index to the end of the cloned belt (desynced pills, stuck at the far
    // end on return). Pause while hidden; re-center and resume when visible.
    this.onVisibility = () => {
      if (document.hidden) {
        this.stopAutoplay();
        if (this.scrollAnim) {
          cancelAnimationFrame(this.scrollAnim);
          this.scrollAnim = null;
          this.track.style.scrollSnapType = '';
        }
      } else {
        this.isProgrammatic = false;
        this.jumpToDom(this.domForReal(this.activeReal));
        this.startAutoplay();
      }
    };
    document.addEventListener('visibilitychange', this.onVisibility);
  }

  disconnectedCallback() {
    this.stopAutoplay();
    if (this.settleTimer) clearTimeout(this.settleTimer);
    if (this.scrollAnim) cancelAnimationFrame(this.scrollAnim);
    if (this.onVisibility) document.removeEventListener('visibilitychange', this.onVisibility);
  }

  // --- index mapping ---
  // Triple-copy belt: DOM slots [0 .. n-1] copy, [n .. 2n-1] real, [2n .. 3n-1] copy.
  // We keep the centered slide within the middle copy and silently shift by one
  // full set (±n) whenever it drifts into an outer copy.
  realForDom(domIndex) {
    if (!this.looping) return domIndex;
    return ((domIndex % this.realCount) + this.realCount) % this.realCount;
  }

  domForReal(realIndex) {
    return this.looping ? this.realCount + realIndex : realIndex;
  }

  // --- geometry ---
  centerOffsetFor(slide) {
    const trackRect = this.track.getBoundingClientRect();
    const slideRect = slide.getBoundingClientRect();
    const delta = slideRect.left + slideRect.width / 2 - (trackRect.left + trackRect.width / 2);
    return this.track.scrollLeft + delta;
  }

  closestDomIndex() {
    const trackRect = this.track.getBoundingClientRect();
    const trackCenter = trackRect.left + trackRect.width / 2;
    let closest = 0;
    let closestDistance = Infinity;
    this.slides.forEach((slide, index) => {
      const rect = slide.getBoundingClientRect();
      const distance = Math.abs(rect.left + rect.width / 2 - trackCenter);
      if (distance < closestDistance) {
        closestDistance = distance;
        closest = index;
      }
    });
    return closest;
  }

  // --- visual emphasis (scale/opacity of the centered card) ---
  applyEmphasis(domIndex) {
    this.slides.forEach((slide, i) => {
      slide.classList.toggle('is-centered', i === domIndex);
    });
  }

  // --- progress pills ---
  // animate === false -> snap the active pill full instantly
  // animate === <ms>  -> fill the active pill over that duration
  setPill(realIndex, animate) {
    this.segments.forEach((segment) => {
      segment.classList.add('no-transition');
      segment.classList.remove('is-active');
    });
    if (this.segments[0]) void this.segments[0].offsetWidth;

    const active = this.segments[realIndex];
    if (active) {
      if (animate) {
        active.style.setProperty('--fill-duration', `${animate}ms`);
        active.classList.remove('no-transition');
        void active.offsetWidth;
        active.classList.add('is-active');
      } else {
        active.style.removeProperty('--fill-duration');
        active.classList.add('is-active');
      }
    }

    requestAnimationFrame(() => {
      this.segments.forEach((segment) => segment.classList.remove('no-transition'));
    });
  }

  // --- navigation (DOM-index based so looping always moves forward
  // into the adjacent clone instead of rewinding across the whole belt) ---
  jumpToDom(domIndex) {
    const slide = this.slides[domIndex];
    if (!slide) return;
    this.track.scrollTo({ left: this.centerOffsetFor(slide), behavior: 'instant' });
    this.currentDom = domIndex;
    this.activeReal = this.realForDom(domIndex);
    this.applyEmphasis(domIndex);
  }

  glideToDom(domIndex) {
    const slide = this.slides[domIndex];
    if (!slide) return;
    this.currentDom = domIndex;
    this.activeReal = this.realForDom(domIndex);
    this.isProgrammatic = true;
    // Custom eased glide (longer + smoother than the browser's quick native
    // smooth-scroll) for a more stylish belt transition.
    this.animateScrollTo(this.centerOffsetFor(slide), 750);
  }

  animateScrollTo(target, duration) {
    if (this.scrollAnim) cancelAnimationFrame(this.scrollAnim);
    const start = this.track.scrollLeft;
    const delta = target - start;
    if (Math.abs(delta) < 1) {
      // Already centered: no scroll events will fire, so the usual
      // scroll -> settle chain never runs. Settle explicitly, otherwise
      // isProgrammatic stays true forever (pill updates freeze) and autoplay
      // never resumes after a stopAutoplay() (e.g. clicking the active pill).
      if (this.settleTimer) clearTimeout(this.settleTimer);
      this.settleTimer = setTimeout(() => this.onSettle(), 0);
      return;
    }
    // The track uses scroll-snap-type: x mandatory. If left on, the browser
    // tries to snap on every frame while our rAF sets scrollLeft, and the two
    // fight → janky/stuttering glide. Disable snap for the duration of the
    // animation and restore it once we've landed exactly on target.
    this.track.style.scrollSnapType = 'none';
    // easeInOutCubic — gentle acceleration and settle
    const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
    let startTime = null;
    const step = (now) => {
      if (startTime === null) startTime = now;
      const elapsed = now - startTime;
      const t = Math.min(elapsed / duration, 1);
      this.track.scrollLeft = start + delta * ease(t);
      if (t < 1) {
        this.scrollAnim = requestAnimationFrame(step);
      } else {
        this.scrollAnim = null;
        this.track.style.scrollSnapType = '';
      }
    };
    this.scrollAnim = requestAnimationFrame(step);
  }

  advance(step) {
    this.glideToDom(this.currentDom + step);
  }

  setupScrollTracking() {
    this.track.addEventListener(
      'scroll',
      () => {
        if (!this.isProgrammatic) {
          const domIndex = this.closestDomIndex();
          this.applyEmphasis(domIndex);
          const real = this.realForDom(domIndex);
          if (real !== this.activeReal) {
            this.activeReal = real;
            this.setPill(real, false);
          }
        }
        if (this.settleTimer) clearTimeout(this.settleTimer);
        this.settleTimer = setTimeout(() => this.onSettle(), 150);
      },
      { passive: true }
    );
  }

  onSettle() {
    let domIndex = this.closestDomIndex();

    // Silent wraparound: keep the centered slide inside the middle copy by
    // shifting a whole set at a time. The destination shows the same image
    // with identical neighbours, so the jump is invisible.
    if (this.looping) {
      while (domIndex < this.realCount) domIndex += this.realCount;
      while (domIndex >= this.realCount * 2) domIndex -= this.realCount;
      if (domIndex !== this.closestDomIndex()) {
        this.track.scrollTo({ left: this.centerOffsetFor(this.slides[domIndex]), behavior: 'instant' });
      }
    }

    this.currentDom = domIndex;
    this.activeReal = this.realForDom(domIndex);
    this.applyEmphasis(domIndex);
    this.isProgrammatic = false;

    if (!this.isDragging && !this.autoplayActive) this.startAutoplay();
  }

  startAutoplay() {
    this.stopAutoplay();
    if (!this.looping || this.realCount <= 1) return;
    if (document.hidden) return; // visibilitychange will restart on return
    this.autoplayActive = true;
    this.scheduleFill();
  }

  scheduleFill() {
    this.setPill(this.activeReal, this.autoplayDelay);
    this.autoplayTimer = setTimeout(() => {
      this.advance(1); // always forward, into the trailing clone when needed
      this.scheduleFill();
    }, this.autoplayDelay);
  }

  stopAutoplay() {
    this.autoplayActive = false;
    if (this.autoplayTimer) {
      clearTimeout(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  setupArrows() {
    if (this.prevButton) {
      this.prevButton.addEventListener('click', () => {
        this.stopAutoplay();
        this.advance(-1);
      });
    }
    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => {
        this.stopAutoplay();
        this.advance(1);
      });
    }
  }

  setupProgressClicks() {
    this.segments.forEach((segment, index) => {
      segment.addEventListener('click', () => {
        this.stopAutoplay();
        this.glideToDom(this.domForReal(index));
      });
    });
  }

  setupDrag() {
    let startX = 0;
    let startScrollLeft = 0;
    let moved = false;
    let downSlide = null;

    this.track.addEventListener('pointerdown', (event) => {
      this.isDragging = true;
      this.isProgrammatic = false;
      moved = false;
      // Remember the pressed slide NOW: setPointerCapture below retargets the
      // eventual click event to the track itself (click is a PointerEvent in
      // Chromium), so the click handler can't see which slide was hit.
      downSlide = event.target.closest ? event.target.closest('.promo-scroll__slide') : null;
      startX = event.clientX;
      startScrollLeft = this.track.scrollLeft;
      this.track.classList.add('is-dragging');
      this.track.setPointerCapture(event.pointerId);
      this.stopAutoplay();
    });

    this.track.addEventListener('pointermove', (event) => {
      if (!this.isDragging) return;
      const delta = event.clientX - startX;
      if (Math.abs(delta) > 5) moved = true;
      this.track.scrollLeft = startScrollLeft - delta;
    });

    const stopDrag = () => {
      if (!this.isDragging) return;
      this.isDragging = false;
      this.track.classList.remove('is-dragging');
      if (this.settleTimer) clearTimeout(this.settleTimer);
      this.settleTimer = setTimeout(() => this.onSettle(), 150);
    };
    this.track.addEventListener('pointerup', stopDrag);
    this.track.addEventListener('pointercancel', stopDrag);
    this.track.addEventListener('pointerleave', stopDrag);

    this.track.addEventListener(
      'click',
      (event) => {
        if (moved) {
          event.preventDefault();
          return;
        }
        // Side (blurred) slides act like the arrows: swallow the link and
        // glide the belt to the clicked slide. Only the centered slide
        // behaves as a real link.
        const slide = event.target.closest('.promo-scroll__slide') || downSlide;
        downSlide = null;
        if (!slide) return;
        const domIndex = this.slides.indexOf(slide);
        if (domIndex === -1) return;
        if (domIndex === this.currentDom) {
          // When the click was retargeted to the track (pointer capture),
          // the link's native activation is lost - follow it manually.
          if (!slide.contains(event.target) && slide.href) window.location.href = slide.href;
          return;
        }
        event.preventDefault();
        this.stopAutoplay();
        this.glideToDom(domIndex);
      },
      true
    );
  }

  setupWheel() {
    this.track.addEventListener(
      'wheel',
      (event) => {
        // Only hijack genuine HORIZONTAL intent (trackpad side-swipe).
        // Vertical wheel is left alone so the page scrolls normally.
        if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
        event.preventDefault();
        this.stopAutoplay();
        this.isProgrammatic = false;
        this.track.scrollLeft += event.deltaX;
      },
      { passive: false }
    );
  }
}

customElements.define('promo-scroll-strip', PromoScrollStrip);
